/**
 * VideoRow — one clip in a list: its picture, its title, whose it is, how long.
 *
 * Task 13. The same row in the «Видео» tab and in the results of «Поиск», so a clip looks the
 * same wherever it is found — the way `TrackRow` is one row for every list of tracks. A YouTube
 * link the server vouched for is a button, and since task 14 so is an mp4 it handed an address
 * for; `onPlay` says what the tap does where the row is (the tab plays it in its player area,
 * search opens it there). A row the server could not vouch for is drawn and not pressable: a
 * row that went nowhere would be worse than one that says what it is.
 *
 * Task 77: in the «Видео» tab a row ends with a heart, as a track row does (`onHeart`); the
 * results of «Поиск» draw none.
 */

import type { JSX } from 'react';
import { memo, useCallback } from 'react';

import type { VideoItem } from '../lib/api';
import { formatDuration } from '../lib/format';
import { isPlayable } from '../store/video';

import { Cover } from './Cover';
import { FavoriteButton } from './FavoriteButton';

/** The word before a link's channel. A name, not copy: it stays as YouTube spells it. */
export const YOUTUBE_WORD = 'YouTube';

/** «YouTube · канал» for a link, the billing for an mp4, nothing when there is neither. */
export function videoByline(item: VideoItem): string {
  return [item.kind === 'youtube' ? YOUTUBE_WORD : '', item.performer]
    .filter((part) => part !== '')
    .join(' · ');
}

export interface VideoRowProps {
  readonly item: VideoItem;
  /** This is the clip in the frame: drawn in gold, like the track that is playing. */
  readonly current?: boolean | undefined;
  readonly onPlay: (item: VideoItem) => void;
  /** Task 77: a heart at the row's end; told after a tap, with the heart's new state. */
  readonly onHeart?: ((item: VideoItem, on: boolean) => void) | undefined;
}

export const VideoRow = memo(function VideoRow({
  item,
  current = false,
  onPlay,
  onHeart,
}: VideoRowProps): JSX.Element {
  const byline = videoByline(item);
  const duration = formatDuration(item.durationS);
  const hearted = useCallback(
    (on: boolean): void => {
      onHeart?.(item, on);
    },
    [item, onHeart],
  );
  const body = (
    <>
      {/* Task 72: the length on the picture, as on a video site; YouTube's own has none here. */}
      <span className="videoThumbBox">
        <Cover src={item.cover} className="videoThumb" />
        {duration !== '' && <span className="videoThumbTime">{duration}</span>}
      </span>
      <span className="trackText">
        <span className="trackTitle">{item.title}</span>
        {byline !== '' && <span className="trackByline">{byline}</span>}
      </span>
    </>
  );

  return (
    <li className="row" data-video-id={item.id}>
      {isPlayable(item) ? (
        <button
          type="button"
          className={current ? 'rowButton rowCurrent' : 'rowButton'}
          aria-current={current ? 'true' : undefined}
          onClick={() => {
            onPlay(item);
          }}
        >
          {body}
        </button>
      ) : (
        <div className="videoRow">{body}</div>
      )}
      {onHeart !== undefined && (
        <FavoriteButton
          trackId={item.id}
          title={item.title}
          className="rowFav"
          iconClassName="rowFavIcon"
          onToggle={hearted}
        />
      )}
    </li>
  );
});
